import React, { useState, useEffect } from 'react';
import { Shield, LogOut, Mail, Phone, MapPin, Calendar, Users, PoundSterling, Home, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEnquiriesPage = () => {
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('admin_api_key') || '');
  const [authed, setAuthed] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(null);

  const fetchEnquiries = async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/travel-enquiry`, { params: { api_key: key } });
      setEnquiries(res.data.enquiries || []);
      setAuthed(true);
      localStorage.setItem('admin_api_key', key);
    } catch (err) {
      setError(err.response?.status === 403 ? 'Invalid admin key.' : 'Failed to load enquiries.');
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiKey) fetchEnquiries(apiKey);
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    setApiKey('');
    setAuthed(false);
    setEnquiries([]);
    localStorage.removeItem('admin_api_key');
  };

  const sorted = [...enquiries].sort((a, b) => {
    const av = a[sortBy] ?? '';
    const bv = b[sortBy] ?? '';
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return iso; }
  };

  // ---------- Auth screen ----------
  if (!authed) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0F172A',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem'
      }}>
        <div style={{
          background: '#FFFCF5', padding: '2.5rem',
          borderRadius: '4px', border: '1px solid #C5A059',
          maxWidth: '400px', width: '100%'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <Shield size={40} color="#C5A059" style={{ marginBottom: '1rem' }} />
            <div style={{
              fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: '#C5A059', marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              Admin Access
            </div>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              color: '#0F172A', margin: 0, fontSize: '1.5rem', fontWeight: 700
            }}>
              Travel Enquiries
            </h1>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); fetchEnquiries(apiKey); }}>
            <label style={{
              display: 'block', fontSize: '0.72rem', fontWeight: 600,
              letterSpacing: '0.15em', textTransform: 'uppercase',
              color: '#0F172A', marginBottom: '0.5rem', fontFamily: "'Inter', sans-serif"
            }}>
              Admin Key
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="admin-key-input"
              autoFocus
              style={{
                width: '100%', padding: '0.85rem 1rem',
                border: '1px solid rgba(15, 23, 42, 0.15)', borderRadius: '3px',
                fontSize: '0.95rem', fontFamily: "'Inter', sans-serif",
                color: '#0F172A', background: '#FFFCF5', outline: 'none',
                boxSizing: 'border-box', marginBottom: '1rem'
              }}
              placeholder="Enter admin key"
            />
            {error && (
              <div data-testid="admin-error" style={{
                color: '#0F172A', background: 'rgba(197,160,89,0.15)',
                padding: '0.6rem 1rem', fontSize: '0.85rem', marginBottom: '1rem',
                borderLeft: '3px solid #C5A059', fontFamily: "'Inter', sans-serif"
              }}>{error}</div>
            )}
            <button
              type="submit"
              disabled={loading || !apiKey}
              data-testid="admin-login-btn"
              style={{
                width: '100%', background: '#0F172A', color: '#FFFCF5',
                padding: '0.9rem', border: 'none', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.1em',
                textTransform: 'uppercase', borderRadius: '3px',
                fontFamily: "'Inter', sans-serif",
                opacity: (loading || !apiKey) ? 0.6 : 1
              }}
            >
              {loading ? 'Verifying...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ---------- Dashboard ----------
  const cell = { padding: '0.9rem 1rem', borderBottom: '1px solid rgba(15,23,42,0.08)', fontSize: '0.88rem', color: '#0F172A', fontFamily: "'Inter', sans-serif", verticalAlign: 'top' };
  const th = { ...cell, background: '#0F172A', color: '#C5A059', fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', userSelect: 'none', borderBottom: 'none' };

  return (
    <div style={{ minHeight: '100vh', background: '#FFFCF5', padding: '2rem 1rem' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem'
        }}>
          <div>
            <div style={{
              fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: '#C5A059', marginBottom: '0.5rem',
              fontFamily: "'Inter', sans-serif"
            }}>
              Abba Academy · Admin
            </div>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              color: '#0F172A', margin: 0, fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: 700, letterSpacing: '-0.02em'
            }}>
              Travel Tutor <span style={{ color: '#C5A059', fontStyle: 'italic' }}>Enquiries</span>
            </h1>
            <p style={{ color: '#475569', margin: '0.4rem 0 0 0', fontFamily: "'Inter', sans-serif", fontSize: '0.9rem' }}>
              {enquiries.length} total · Sorted by {sortBy.replace('_', ' ')} ({sortDir})
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => fetchEnquiries(apiKey)}
              data-testid="refresh-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'transparent', color: '#0F172A',
                padding: '0.7rem 1.2rem', border: '1px solid #0F172A',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                fontFamily: "'Inter', sans-serif", borderRadius: '3px',
                letterSpacing: '0.02em'
              }}
            >
              <RefreshCw size={16} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                background: 'transparent', color: '#0F172A',
                padding: '0.7rem 1.2rem', border: '1px solid rgba(15,23,42,0.15)',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                fontFamily: "'Inter', sans-serif", borderRadius: '3px'
              }}
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>

        {/* Table on desktop / cards on mobile */}
        {enquiries.length === 0 ? (
          <div style={{
            padding: '4rem 2rem', textAlign: 'center', background: '#FFFCF5',
            border: '1px dashed rgba(15,23,42,0.15)', borderRadius: '4px'
          }}>
            <p style={{ color: '#64748B', margin: 0, fontFamily: "'Inter', sans-serif" }}>
              No enquiries yet. Once a family submits the confidential form,
              you'll see them here first.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div style={{ display: 'block', overflow: 'auto' }} className="admin-table-wrap">
              <table style={{
                width: '100%', borderCollapse: 'collapse',
                background: '#FFFCF5',
                border: '1px solid rgba(197,160,89,0.3)'
              }}>
                <thead>
                  <tr>
                    <th style={th} onClick={() => toggleSort('created_at')}>Received</th>
                    <th style={th} onClick={() => toggleSort('family_name')}>Family</th>
                    <th style={th} onClick={() => toggleSort('current_location')}>Location</th>
                    <th style={th} onClick={() => toggleSort('preferred_start')}>Start</th>
                    <th style={th} onClick={() => toggleSort('budget_range')}>Budget</th>
                    <th style={th}>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((e, i) => (
                    <tr
                      key={i}
                      data-testid={`enquiry-row-${i}`}
                      onClick={() => setSelected(e)}
                      style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                      onMouseEnter={(ev) => ev.currentTarget.style.background = 'rgba(197,160,89,0.06)'}
                      onMouseLeave={(ev) => ev.currentTarget.style.background = 'transparent'}
                    >
                      <td style={cell}>{formatDate(e.created_at)}</td>
                      <td style={{ ...cell, fontWeight: 600 }}>{e.family_name}</td>
                      <td style={cell}>{e.current_location}</td>
                      <td style={cell}>{e.preferred_start}</td>
                      <td style={{ ...cell, color: '#C5A059', fontWeight: 600 }}>{e.budget_range}</td>
                      <td style={cell}>
                        <a
                          href={`mailto:${e.contact_email}`}
                          onClick={(ev) => ev.stopPropagation()}
                          style={{ color: '#0F172A', textDecoration: 'none', display: 'inline-flex', gap: '0.3rem', alignItems: 'center' }}
                        >
                          <Mail size={14} /> Email
                        </a>
                        {e.contact_phone && (
                          <a
                            href={`tel:${e.contact_phone}`}
                            onClick={(ev) => ev.stopPropagation()}
                            style={{ color: '#0F172A', textDecoration: 'none', display: 'inline-flex', gap: '0.3rem', alignItems: 'center', marginLeft: '0.75rem' }}
                          >
                            <Phone size={14} /> Call
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          data-testid="enquiry-detail-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '2rem 1rem', overflow: 'auto'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#FFFCF5', width: '100%', maxWidth: '720px',
              borderRadius: '4px', border: '1px solid #C5A059',
              marginTop: '2rem'
            }}
          >
            <div style={{
              background: '#0F172A', padding: '1.5rem 2rem',
              borderBottom: '2px solid #C5A059'
            }}>
              <div style={{
                fontSize: '0.7rem', letterSpacing: '0.2em', textTransform: 'uppercase',
                color: '#C5A059', marginBottom: '0.4rem', fontFamily: "'Inter', sans-serif"
              }}>
                Confidential Enquiry
              </div>
              <h2 style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                color: '#FFFCF5', margin: 0, fontSize: '1.5rem', fontWeight: 700
              }}>
                {selected.family_name}
              </h2>
              <p style={{ color: 'rgba(255,252,245,0.6)', margin: '0.3rem 0 0 0', fontSize: '0.85rem', fontFamily: "'Inter', sans-serif" }}>
                Received {formatDate(selected.created_at)}
              </p>
            </div>
            <div style={{ padding: '2rem' }}>
              {[
                { icon: Mail, label: 'Email', value: selected.contact_email, href: `mailto:${selected.contact_email}` },
                { icon: Phone, label: 'Phone', value: selected.contact_phone },
                { icon: Users, label: 'Children', value: selected.number_of_children },
                { icon: MapPin, label: 'Current location', value: selected.current_location },
                { icon: MapPin, label: 'Travel destinations', value: selected.travel_destinations },
                { icon: Calendar, label: 'Preferred start', value: selected.preferred_start },
                { icon: Calendar, label: 'Duration', value: selected.engagement_duration },
                { icon: Home, label: 'Accommodation', value: selected.accommodation_provided },
                { icon: PoundSterling, label: 'Budget', value: selected.budget_range, highlight: true }
              ].filter(r => r.value).map((r, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '1rem', alignItems: 'flex-start',
                  padding: '0.85rem 0', borderBottom: '1px solid rgba(15,23,42,0.06)'
                }}>
                  <r.icon size={16} color="#C5A059" style={{ marginTop: '3px', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                      color: '#64748B', marginBottom: '0.25rem', fontFamily: "'Inter', sans-serif"
                    }}>
                      {r.label}
                    </div>
                    {r.href ? (
                      <a href={r.href} style={{ color: '#C5A059', textDecoration: 'none', fontFamily: "'Inter', sans-serif", wordBreak: 'break-word' }}>{r.value}</a>
                    ) : (
                      <div style={{
                        color: r.highlight ? '#C5A059' : '#0F172A',
                        fontWeight: r.highlight ? 700 : 500,
                        fontFamily: "'Inter', sans-serif", wordBreak: 'break-word'
                      }}>{r.value}</div>
                    )}
                  </div>
                </div>
              ))}

              <div style={{
                marginTop: '1rem', padding: '0.85rem 0',
                borderBottom: '1px solid rgba(15,23,42,0.06)'
              }}>
                <div style={{
                  fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                  color: '#64748B', marginBottom: '0.5rem', fontFamily: "'Inter', sans-serif"
                }}>
                  Focus areas
                </div>
                <div style={{ color: '#0F172A', fontFamily: "'Inter', sans-serif" }}>{selected.subjects_focus}</div>
              </div>

              {selected.additional_notes && (
                <div style={{
                  marginTop: '1.5rem', padding: '1rem 1.25rem',
                  background: 'rgba(197,160,89,0.06)',
                  borderLeft: '3px solid #C5A059',
                  whiteSpace: 'pre-wrap',
                  color: '#0F172A', fontFamily: "'Inter', sans-serif",
                  fontSize: '0.92rem', lineHeight: 1.6
                }}>
                  {selected.additional_notes}
                </div>
              )}

              <div style={{ marginTop: '2rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <a
                  href={`mailto:${selected.contact_email}?subject=Re:%20Your%20Travel%20Tutor%20Enquiry%20-%20Abba%20Academy`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    background: '#0F172A', color: '#FFFCF5',
                    padding: '0.85rem 1.5rem', textDecoration: 'none',
                    fontWeight: 600, fontSize: '0.85rem', letterSpacing: '0.05em',
                    textTransform: 'uppercase', fontFamily: "'Inter', sans-serif",
                    borderRadius: '3px'
                  }}
                >
                  <Mail size={16} /> Reply by Email
                </a>
                <button
                  onClick={() => setSelected(null)}
                  data-testid="close-detail-btn"
                  style={{
                    background: 'transparent', color: '#0F172A',
                    padding: '0.85rem 1.5rem', border: '1px solid rgba(15,23,42,0.15)',
                    cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                    fontFamily: "'Inter', sans-serif", borderRadius: '3px'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEnquiriesPage;
